import React, { useState } from 'react';

interface TeamMember {
    name: string;
    role: string;
    education: string;
    location: string;
    bio: string;
    photo: string;
    photoPosition?: string;
    photoZoom?: number; // 1 = normal, >1 = zoomed in, <1 = zoomed out
}

interface TeamSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromBlue: string;
            toPurple: string;
        };
    };
}

const TeamSection: React.FC<TeamSectionProps> = ({ highlightColor }) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [expandedMember, setExpandedMember] = useState<TeamMember | null>(null);
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

    const handleImageLoad = (index: number) => {
        setLoadedImages(prev => new Set(prev).add(index));
    };

    const teamMembers: TeamMember[] = [
        { 
            name: 'Ryan', 
            role: 'Co-Founder and CEO',
            education: 'MIT',
            location: 'Washington, DC',
            bio: '',
            photo: '/images/team/team-ryan-hetrick.jpg',
            photoPosition: 'center 18%', // Adjust: lower % = higher in frame, higher % = lower in frame
            photoZoom: 1 // 1 = normal, >1 = zoomed in, <1 = zoomed out
        },
        { 
            name: 'Josh', 
            role: 'Co-Founder and CRO',
            education: 'USMA - West Point',
            location: 'Los Angeles, CA',
            bio: '',
            photo: '/images/team/team-joshua-park.jpg',
            photoPosition: 'center 25%',
            photoZoom: 1
        },
        { 
            name: 'Cam', 
            role: 'CTO and Technical Director',
            education: 'RIT',
            location: 'Washington, DC',
            bio: '',
            photo: '/images/team/team-cameron-marcus.jpg',
            photoPosition: 'center 50%',
            photoZoom: 1
        },
        { 
            name: 'D\'errah', 
            role: 'Tech Team',
            education: 'Michigan State',
            location: 'Austin, TX',
            bio: '',
            photo: '/images/team/team-derrah-richardson.jpg',
            photoPosition: 'center 100%',
            photoZoom: 1
        },
        // { 
        //     name: 'Jack', 
        //     role: 'Tech Team',
        //     education: 'USMA - West Point',
        //     location: 'Washington, DC',
        //     bio: '',
        //     photo: '/images/team/team-jack-graham.jpg',
        //     photoPosition: 'center 20%',
        //     photoZoom: 1
        // },
        // { 
        //     name: 'Jamie', 
        //     role: 'Tech Team',
        //     education: 'Columbia',
        //     location: 'Washington, DC',
        //     bio: '',
        //     photo: '/images/team/team-jamie-deantonis.jpeg',
        //     photoPosition: 'center 0%',
        //     photoZoom: 1
        // },
        { 
            name: 'Despina', 
            role: 'Business Development Team',
            education: 'Harvard',
            location: 'Cambridge, MA',
            bio: '',
            photo: '/images/team/team-despina-giannakopoulos.jpg',
            photoPosition: 'center 100%',
            photoZoom: 1
        },
        // { 
        //     name: 'Anna', 
        //     role: 'Creative Director',
        //     education: 'Stanford',
        //     location: 'Los Angeles, CA',
        //     bio: '',
        //     photo: '/images/team/',
        //     photoPosition: 'center 100%',
        //     photoZoom: 1
        // },
        // { 
        //     name: 'Fiene', 
        //     role: 'Marketing Team',
        //     education: 'Harvard',
        //     location: 'Cambridge, MA',
        //     bio: '',
        //     photo: '/images/team/team-fiene-oerlemans.jpg',
        //     photoPosition: 'center 25%',
        //     photoZoom: 1
        // },
        // { 
        //     name: 'Xavier', 
        //     role: 'Marketing Team',
        //     education: 'University of Toledo',
        //     location: 'Columbus, OH',
        //     bio: '',
        //     photo: '/images/team/team-xavier-gallo.jpeg',
        //     photoPosition: 'center 45%',
        //     photoZoom: 1
        // },
        // { 
        //     name: 'Dylan', 
        //     role: 'Marketing Team',
        //     education: 'Harvard',
        //     location: 'San Jose, CA',
        //     bio: '',
        //     photo: '/images/team/',
        //     photoPosition: 'center 100%',
        //     photoZoom: 1
        // },
    ];

    return (
        <>
            <section className="py-32 px-4 sm:px-6 lg:px-8 bg-black relative">
                <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"></div>
                <div className="max-w-7xl mx-auto relative z-1">
                    <div className="pb-12 border-b border-neutral-800">
                        <h2 className="text-4xl md:text-5xl font-bold text-white text-center observe-fade opacity-0">Meet the Team</h2>
                    </div>
                    
                    {/* Team Grid */}
                    <div className="flex flex-wrap justify-center gap-8 mt-12">
                        {teamMembers.map((member, index) => (
                            <div 
                                key={index} 
                                className="relative bg-neutral-900 rounded-xl overflow-hidden border border-neutral-800 cursor-pointer transition-all duration-300 hover:border-neutral-600 hover:shadow-xl group fade-in w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.334rem)]"
                                style={{ 
                                    animationDelay: `${index * 0.2}s`,
                                    opacity: 0
                                }}
                                onMouseEnter={() => setHoveredIndex(index)}
                                onMouseLeave={() => setHoveredIndex(null)}
                                onClick={() => {}/*setExpandedMember(member)*/}
                            >
                                {/* Photo */}
                                <div className="relative w-full h-64 bg-gray-800 overflow-hidden">
                                    <img
                                        src={member.photo}
                                        alt={member.name}
                                        className="w-full h-full object-cover transition-all duration-500"
                                        style={{ 
                                            objectPosition: member.photoPosition || 'center center',
                                            transform: `scale(${member.photoZoom || 1})`,
                                            transformOrigin: 'center center',
                                            opacity: loadedImages.has(index) ? 1 : 0
                                        }}
                                        onLoad={() => handleImageLoad(index)}
                                        onError={(e) => {
                                            // Fallback to gradient placeholder if image fails
                                            (e.target as HTMLImageElement).style.display = 'none';
                                            const parent = (e.target as HTMLImageElement).parentElement;
                                            if (parent) {
                                                parent.style.background = `linear-gradient(135deg, ${highlightColor.gradient.fromBlue}, ${highlightColor.gradient.toPurple})`;
                                            }
                                        }}
                                    />
                                    {/* Gradient overlay on hover */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                </div>
                                
                                {/* Basic Info */}
                                <div className="p-6 text-center">
                                    <h3 className="text-xl font-bold text-white mb-2">{member.name}</h3>
                                    <p className="text-gray-400 text-sm">{member.role}</p>
                                </div>

                                {/* Hover Popup */}
                                {hoveredIndex === index && (
                                    <div className="absolute inset-0 bg-black/95 backdrop-blur-sm p-6 flex flex-col justify-center items-center text-center transition-all duration-300 animate-in fade-in">
                                        <h3 className="text-2xl font-bold text-white mb-2">{member.name}</h3>
                                        <p 
                                            className="text-sm font-semibold mb-3 bg-clip-text text-transparent"
                                            style={{
                                                backgroundImage: `linear-gradient(to right, ${highlightColor.gradient.fromBlue}, ${highlightColor.gradient.toPurple})`,
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent'
                                            }}
                                        >
                                            {member.role}
                                        </p>
                                        <div className="space-y-2 text-gray-300 text-sm">
                                            <p className="flex items-center justify-center gap-2">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                </svg>
                                                {member.education}
                                            </p>
                                            <p className="flex items-center justify-center gap-2">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                {member.location}
                                            </p>
                                        </div>
                                        {/* <p className="text-xs text-gray-400 mt-4">Click to view full profile</p> */}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Expanded Member Modal - Outside section to avoid z-index stacking context issues */}
            {expandedMember && (
                    <div 
                        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 py-16 md:p-8 animate-in fade-in duration-300 overflow-y-auto"
                        onClick={() => setExpandedMember(null)}
                    >
                        <div 
                            className="relative w-full max-w-5xl bg-neutral-900 rounded-xl overflow-hidden border border-neutral-800 animate-in zoom-in-95 duration-300 my-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close Button */}
                            <button
                                onClick={() => setExpandedMember(null)}
                                className="absolute top-4 right-4 z-10 p-3 rounded-full bg-black/80 hover:bg-black text-white transition-all duration-300 hover:scale-110 shadow-lg"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                                {/* Left: Full Photo */}
                                <div className="relative h-80 md:h-auto bg-gray-800">
                                    <img
                                        src={expandedMember.photo}
                                        alt={expandedMember.name}
                                        className="w-full h-full object-cover transition-opacity duration-500"
                                        style={{ 
                                            objectPosition: expandedMember.photoPosition || 'center center',
                                            transform: `scale(${expandedMember.photoZoom || 1})`
                                        }}
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                            const parent = (e.target as HTMLImageElement).parentElement;
                                            if (parent) {
                                                parent.style.background = `linear-gradient(135deg, ${highlightColor.gradient.fromBlue}, ${highlightColor.gradient.toPurple})`;
                                            }
                                        }}
                                    />
                                </div>

                                {/* Right: Details */}
                                <div className="p-8 md:p-12 flex flex-col justify-center">
                                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{expandedMember.name}</h2>
                                    <p 
                                        className="text-lg font-semibold mb-6 bg-clip-text text-transparent"
                                        style={{
                                            backgroundImage: `linear-gradient(to right, ${highlightColor.gradient.fromBlue}, ${highlightColor.gradient.toPurple})`,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}
                                    >
                                        {expandedMember.role}
                                    </p>
                                    
                                    <div className="space-y-3 mb-6 text-gray-300">
                                        <div className="flex items-start gap-3">
                                            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                            </svg>
                                            <span>{expandedMember.education}</span>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            <span>{expandedMember.location}</span>
                                        </div>
                                    </div>

                                    <div className="border-t border-neutral-700 pt-6">
                                        <p className="text-gray-300 leading-relaxed">{expandedMember.bio}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
        </>
    );
};

export default TeamSection;

